'use client';

import { useTranslation } from '@/context/LanguageContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebookF, faWhatsapp, faTelegramPlane } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';

interface ShareSectionProps {
    title: string;
    url: string;
    shareTitle?: string;
    shareDesc?: string;
}

export default function ShareSection({ title, url, shareTitle, shareDesc }: ShareSectionProps) {
    const { language } = useTranslation();

    const defaultShareTitle = shareTitle || (language === 'en' ? 'Share This' : 'Bagikan Ini');
    const defaultShareDesc = shareDesc || (language === 'en' ? 'Share this information with your friends and colleagues' : 'Sebarkan informasi ke teman dan kolega Anda');

    return (
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{defaultShareTitle}</h4>
                    <p className="text-sm text-gray-500">{defaultShareDesc}</p>
                </div>
                <div className="flex items-center gap-2">
                    {/* Facebook */}
                    <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-11 h-11 bg-blue-600 text-white rounded-lg flex items-center justify-center hover:bg-blue-700 transition"
                        title="Facebook"
                    >
                        <FontAwesomeIcon icon={faFacebookF} />
                    </a>

                    {/* X (Twitter) */}
                    <a
                        href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-11 h-11 bg-black text-white rounded-lg flex items-center justify-center hover:bg-gray-800 transition"
                        title="X (Twitter)"
                    >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                    </a>

                    {/* WhatsApp */}
                    <a
                        href={`https://wa.me/?text=${encodeURIComponent(`${title} - ${url}`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-11 h-11 bg-green-500 text-white rounded-lg flex items-center justify-center hover:bg-green-600 transition"
                        title="WhatsApp"
                    >
                        <FontAwesomeIcon icon={faWhatsapp} />
                    </a>

                    {/* Telegram */}
                    <a
                        href={`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-11 h-11 bg-sky-500 text-white rounded-lg flex items-center justify-center hover:bg-sky-600 transition"
                        title="Telegram"
                    >
                        <FontAwesomeIcon icon={faTelegramPlane} />
                    </a>

                    {/* Email */}
                    <a
                        href={`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`Check this out: ${title}\n\n${url}`)}`}
                        className="w-11 h-11 bg-gray-600 text-white rounded-lg flex items-center justify-center hover:bg-gray-700 transition"
                        title="Email"
                    >
                        <FontAwesomeIcon icon={faEnvelope} />
                    </a>
                </div>
            </div>
        </div>
    );
}
